import React, { useContext } from 'react';
import { motion } from 'framer-motion';
import { Users2, Building2, Briefcase, Scale, ShieldCheck, FileCheck2 } from 'lucide-react';
import { LangContext } from '../App';
import { translations } from '../lib/translations';

const ICONS = [Users2, Building2, Briefcase, Scale, ShieldCheck, FileCheck2];

const containerVariants = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.1,
    },
  },
};

const cardVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] },
  },
};

export default function PracticeAreas() {
  const lang = useContext(LangContext);
  const t = translations[lang].areas;

  return (
    <section
      id="areas"
      className="py-28 bg-[#F8F5F0]"
      data-testid="practice-areas-section"
    >
      <div className="max-w-6xl mx-auto px-6 lg:px-8">
        {/* Header */}
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.4 }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
        >
          <p className="font-sans text-xs tracking-[0.3em] uppercase text-[#6B2D3E] mb-5">
            — {t.sectionLabel} —
          </p>
          <h2 className="font-serif text-4xl sm:text-5xl text-[#1A1A1A] tracking-tight mb-4">
            {t.title}
          </h2>
          <p className="font-sans text-base text-[#4A4A4A] max-w-md mx-auto">
            {t.subtitle}
          </p>
        </motion.div>

        {/* Grid */}
        <motion.div
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-[#E5E0D8]"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
          data-testid="practice-areas-grid"
        >
          {t.items.map((item, i) => {
            const Icon = ICONS[i];
            return (
              <motion.div
                key={i}
                variants={cardVariants}
                className="bg-[#F8F5F0] p-8 xl:p-10 group hover:bg-white transition-colors duration-400 cursor-default"
                data-testid={`practice-area-card-${i}`}
              >
                <div className="mb-5">
                  <Icon
                    size={22}
                    className="text-[#6B2D3E] group-hover:scale-110 transition-transform duration-300"
                    strokeWidth={1.5}
                  />
                </div>
                <h3 className="font-serif text-xl text-[#1A1A1A] mb-3 tracking-tight">
                  {item.title}
                </h3>
                <div className="w-6 h-px bg-[#6B2D3E] mb-4 group-hover:w-10 transition-all duration-400" />
                <p className="font-sans text-sm text-[#4A4A4A] leading-relaxed">
                  {item.desc}
                </p>
              </motion.div>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
}
